import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import styles from './CookieConsent.module.css';
import {
  getCookieConsent,
  setCookieConsent,
  COOKIE_CONSENT_RESET_EVENT,
} from './cookieConsentStorage';
import { persistPendingSignupAttribution, clearSignupAttribution } from '../../utils/signupAttribution';

// Mesmos prefixos definidos em public/index.html (window.gabrielaIsPatientRoute).
// Mantidos aqui como fallback caso o script inline não tenha rodado ainda.
const PATIENT_ROUTE_PREFIXES = ['/agendar', '/confirmar/', '/confirmar-demo', '/avaliacao', '/cadastro/'];

function isPatientRoute(pathname) {
  if (typeof window !== 'undefined' && typeof window.gabrielaIsPatientRoute === 'function') {
    return window.gabrielaIsPatientRoute(pathname);
  }
  return PATIENT_ROUTE_PREFIXES.some((prefix) => (pathname || '').startsWith(prefix));
}

/**
 * Banner de consentimento de cookies (LGPD).
 *
 * Fica oculto em rotas públicas voltadas ao paciente (agendamento, confirmação,
 * avaliação) e some assim que o usuário decide, guardando a escolha em
 * localStorage (ver cookieConsentStorage.js). "Aceitar todos" libera os
 * trackers de marketing/analytics (window.gabrielaLoadTrackers, definido em
 * public/index.html); sem aceite o Google tag segue em Consent Mode negado, sem
 * cookies, e "Somente necessários" volta a negar se antes havia aceite.
 */
function CookieConsent() {
  const location = useLocation();
  const [consent, setConsent] = useState(() => getCookieConsent());

  useEffect(() => {
    const handleReset = () => setConsent(null);
    window.addEventListener(COOKIE_CONSENT_RESET_EVENT, handleReset);
    return () => window.removeEventListener(COOKIE_CONSENT_RESET_EVENT, handleReset);
  }, []);

  if (consent === 'all' || consent === 'necessary') {
    return null;
  }

  if (isPatientRoute(location.pathname)) {
    return null;
  }

  const handleAcceptAll = () => {
    setCookieConsent('all');
    persistPendingSignupAttribution();
    if (window.gabrielaLoadTrackers) {
      window.gabrielaLoadTrackers();
    }
    setConsent('all');
  };

  const handleNecessaryOnly = () => {
    setCookieConsent('necessary');
    clearSignupAttribution();
    if (window.gabrielaDenyTrackers) {
      window.gabrielaDenyTrackers();
    }
    setConsent('necessary');
  };

  return (
    <div className={styles.banner} role="dialog" aria-live="polite" aria-label="Consentimento de cookies">
      <p className={styles.text}>
        Usamos cookies necessários para o funcionamento da plataforma e, com a sua permissão,
        cookies de análise e marketing (Google e Meta).{' '}
        <a
          href="/terms-privacy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Política de privacidade
        </a>
      </p>
      <div className={styles.actions}>
        <Button
          variant="text"
          onClick={handleNecessaryOnly}
          className={styles.secondaryButton}
        >
          Somente necessários
        </Button>
        <Button
          variant="contained"
          onClick={handleAcceptAll}
          className={styles.primaryButton}
          disableElevation
        >
          Aceitar todos
        </Button>
      </div>
    </div>
  );
}

export default CookieConsent;
